import { httpLink } from "@trpc/client";
import { trpcNoInputSentinel } from "@libs/wire";
import type { ApiTransport } from "../../runtime/ports";

export function httpTransport({
  url,
  fetch: request,
}: {
  url: string;
  fetch?: typeof fetch;
}): ApiTransport {
  const TRPC_REQUEST_TIMEOUT_MS = 15_000;

  const fetchWithTimeout: typeof fetch = (url, options) => {
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      TRPC_REQUEST_TIMEOUT_MS,
    );

    // Forward React Query's abort signal (component unmount, etc.)
    const abort = () => controller.abort();
    if (options?.signal?.aborted) abort();
    else options?.signal?.addEventListener("abort", abort);

    const fetchOptions = { ...options, signal: controller.signal };
    if (fetchOptions.body == null) {
      const method = fetchOptions.method?.toUpperCase();
      const headers = new Headers(fetchOptions.headers);
      if (
        method &&
        method !== "GET" &&
        method !== "HEAD" &&
        headers.get("content-type")?.includes("application/json")
      ) {
        fetchOptions.body = JSON.stringify(trpcNoInputSentinel);
      } else {
        delete fetchOptions.body;
      }
    }

    return (request ?? fetch)(url, fetchOptions).finally(() => {
      clearTimeout(timeout);
      options?.signal?.removeEventListener("abort", abort);
    });
  };

  return ({ headers }) =>
    httpLink({
      fetch: fetchWithTimeout,
      transformer: {
        input: {
          serialize: (object) => {
            // TRPC client sending data
            // TRPC client sends data as string with quotes when batching is off so we are overriding the serialize method to send data as object
            // Also since TRPC client sets content-type to application/json, we need to actually send valid JSON
            if (typeof object === "string") {
              return { internalData: object };
            } else {
              return object;
            }
          },
          deserialize: (object) => {
            // TRPC server receiving data
            if (typeof object === "object" && "internalData" in object) {
              return object.internalData;
            } else {
              return object;
            }
          },
        },
        output: {
          // TRPC server sending data
          serialize: (object) => object,
          // TRPC client receiving data
          deserialize: (object) => object,
        },
      },
      url,
      headers: ({ op }) => headers(op),
    });
}
